// src/pages/UserManagement.js - Complete Enhanced Version with Bug Fixes
import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Tabs,
  Tab,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  LinearProgress,
  Alert,
  Avatar,
  TablePagination,
  InputAdornment,
  FormHelperText,
  Divider,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Tooltip,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  People as PeopleIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Business as FacilityIcon,
  Security as SecurityIcon,
  LocalHospital as MedicalIcon,
  VpnKey as VpnIcon,
  Phone as PhoneIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { userService } from "../../services/userService";
import { facilityService } from "../../services/facilityService";
import { medicalProfessionalsService } from "../../services/medicalProfessionalsService";
import { useAuth } from "../../contexts/AuthContext";

// TabPanel component for the tabs
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const UserManagement = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Data states
  const [users, setUsers] = useState({
    all: [],
    pending: [],
    admins: [],
    medicalProfessionals: [],
  });
  const [facilities, setFacilities] = useState([]);

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [selectedMP, setSelectedMP] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [generatedApiKey, setGeneratedApiKey] = useState("");

  const { user: currentUser, isSuperAdmin, getUserFacilityId } = useAuth();

  const [resetPasswordDialog, setResetPasswordDialog] = useState({
    open: false,
    user: null,
  });
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // ADD THIS LINE

  // Enhanced form state for creating new users
  const [newUser, setNewUser] = useState({
    // Basic user info
    username: "",
    email: "",
    password: "",
    full_name: "",
    phone_number: "",
    address: "",
    role: "facility_admin",

    // Facility assignment
    facility_id: "",

    // Admin-specific fields
    company_name: "",
    business_license: "",

    // Medical professional-specific fields
    specialty: "",
    medical_license: "",
    mp_category: "nurse",
    medical_license_number: "",
    npi_number: "",

    // Security settings - UPDATED
    vpn_required: false,
    allowed_ip_ranges: "",
    setup_2fa: true,

    // Email settings
    send_welcome_email: true,
    send_activation_email: false,
  });

  // Form validation state
  const [validationErrors, setValidationErrors] = useState({});

  // Medical Professional Categories
  const MP_CATEGORIES = [
    { value: "nurse", label: "Nurse" },
    { value: "doctor", label: "Doctor" },
    { value: "technician", label: "Technician" },
    { value: "phlebotomist", label: "Phlebotomist" },
    { value: "other", label: "Other" },
  ];

  // User Roles based on current user permissions
  const getUserRoles = () => {
    if (isSuperAdmin) {
      return [
        { value: "super_admin", label: "Super Admin" },
        { value: "facility_admin", label: "Facility Admin" },
        { value: "admin", label: "Admin" },
        { value: "medical_professional", label: "Medical Professional" },
      ];
    } else {
      return [
        { value: "facility_admin", label: "Facility Admin" },
        { value: "medical_professional", label: "Medical Professional" },
      ];
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchData();
    }
  }, [isSuperAdmin]);

  // Auto-set facility for non-super admins
  useEffect(() => {
    if (!isSuperAdmin && getUserFacilityId() && !newUser.facility_id) {
      setNewUser((prev) => ({ ...prev, facility_id: getUserFacilityId() }));
    }
  }, [isSuperAdmin, getUserFacilityId, newUser.facility_id]);

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      // Fetch users and facilities in parallel
      const [allUsers, pendingUsers, facilitiesData] = await Promise.all([
        userService.getAllUsers(),
        userService.getPendingUsers(),
        facilityService.getFacilities(),
      ]);

      // Filter for different user types
      const admins = allUsers.filter((user) =>
        ["admin", "facility_admin", "super_admin"].includes(user.role)
      );
      const medicalProfessionals = allUsers.filter(
        (user) => user.role === "medical_professional"
      );

      setUsers({
        all: allUsers,
        pending: pendingUsers || [],
        admins: admins,
        medicalProfessionals: medicalProfessionals,
      });

      setFacilities(facilitiesData || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // UPDATED: Simplified validation - no IP ranges requirement
  const validateForm = () => {
    const errors = {};

    // Basic validation
    if (!newUser.username.trim()) {
      errors.username = "Username is required";
    } else if (newUser.username.length < 3) {
      errors.username = "Username must be at least 3 characters";
    }

    if (!newUser.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newUser.email)) {
      errors.email = "Invalid email format";
    }

    if (!newUser.send_activation_email && !newUser.password) {
      errors.password =
        "Password is required when not sending activation email";
    } else if (newUser.password && newUser.password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    }

    if (!newUser.full_name.trim()) {
      errors.full_name = "Full name is required";
    }

    if (!newUser.role) {
      errors.role = "Role is required";
    }

    // Facility assignment validation
    if (!newUser.facility_id && newUser.role !== "super_admin") {
      errors.facility_id = "Facility assignment is required";
    }

    // Role-specific validation
    if (newUser.role === "medical_professional") {
      if (!newUser.mp_category) {
        errors.mp_category = "Medical professional category is required";
      }
      if (!newUser.specialty.trim()) {
        errors.specialty = "Specialty is required for medical professionals";
      }
    }

    if (newUser.role === "facility_admin" || newUser.role === "admin") {
      if (!newUser.company_name.trim()) {
        errors.company_name = "Company name is required for admins";
      }
    }

    // UPDATED: Phone number validation (required for all users)
    if (!newUser.phone_number.trim()) {
      errors.phone_number = "Phone number is required for SMS 2FA";
    } else if (
      !/^[\+]?[1-9][\d]{0,15}$/.test(
        newUser.phone_number.replace(/[\s\-\(\)]/g, "")
      )
    ) {
      errors.phone_number = "Invalid phone number format";
    }

    // UPDATED: VPN IP validation (only if VPN is enabled and IP ranges provided)
    if (newUser.vpn_required && newUser.allowed_ip_ranges.trim()) {
      const ipRanges = newUser.allowed_ip_ranges
        .split(",")
        .map((ip) => ip.trim())
        .filter((ip) => ip);
      for (const ip of ipRanges) {
        if (ip && !isValidIPRange(ip)) {
          errors.allowed_ip_ranges = "Invalid IP address or range format";
          break;
        }
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isValidIPRange = (ip) => {
    // Enhanced IP validation
    const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
    const ipv6Regex = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}(\/\d{1,3})?$/;

    if (ipv4Regex.test(ip)) {
      // Additional IPv4 validation
      const parts = ip.split("/")[0].split(".");
      return parts.every(
        (part) => parseInt(part) >= 0 && parseInt(part) <= 255
      );
    }

    return ipv6Regex.test(ip);
  };

  const handleApproveUser = async (userId) => {
    try {
      await userService.approveUser(userId);
      setSuccess("User approved successfully");
      await fetchData();
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error approving user:", error);
      setError(error.message || "Failed to approve user");
    }
  };

  const handleRejectUser = async (userId) => {
    try {
      await userService.rejectUser(userId);
      setSuccess("User rejected successfully");
      await fetchData();
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error rejecting user:", error);
      setError(error.message || "Failed to reject user");
    }
  };

  const handleDeleteUser = async () => {
    try {
      await userService.deleteUser(deleteDialog.user.id);
      setSuccess(`User "${deleteDialog.user.full_name}" deleted successfully`);
      setDeleteDialog({ open: false, user: null });
      await fetchData();
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error deleting user:", error);
      setError(error.message || "Failed to delete user");
      setDeleteDialog({ open: false, user: null });
    }
  };

  const handleResetPassword = async () => {
    try {
      setResetPasswordLoading(true);
      setError("");

      // Validation
      if (!newPassword || newPassword.length < 8) {
        setError("Password must be at least 8 characters");
        return;
      }

      if (newPassword !== confirmNewPassword) {
        setError("Passwords don't match");
        return;
      }

      await userService.resetUserPassword(resetPasswordDialog.user.id, {
        new_password: newPassword,
      });

      setSuccess(
        `Password reset successfully for ${resetPasswordDialog.user.full_name}`
      );
      setResetPasswordDialog({ open: false, user: null });
      setNewPassword("");
      setConfirmNewPassword("");
      setShowNewPassword(false); 
      setShowConfirmPassword(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error resetting password:", error);
      setError(error.message || "Failed to reset password");
    } finally {
      setResetPasswordLoading(false);
    }
  };

  // UPDATED: Simplified user creation with auto-generated API key
  const handleCreateUser = async () => {
    try {
      setError("");
      setValidationErrors({});
      setGeneratedApiKey(""); // Reset API key display

      if (!validateForm()) {
        return;
      }

      let userData;
      let response;

      if (newUser.role === "medical_professional") {
        // Create medical professional with custom endpoint
        userData = {
          full_name: newUser.full_name,
          username: newUser.username,
          email: newUser.email,
          password: newUser.send_activation_email
            ? undefined
            : newUser.password,
          phone_number: newUser.phone_number, // ADDED: Required for SMS 2FA
          mp_category: newUser.mp_category,
          medical_license_number: newUser.medical_license_number,
          npi_number: newUser.npi_number,
          facility_id: parseInt(newUser.facility_id),
          send_activation_email: newUser.send_activation_email,
          send_welcome_email: newUser.send_welcome_email,
          isCustom: true,
          // ADDED: Security settings
          vpn_required: newUser.vpn_required || false,
          allowed_ip_ranges:
            newUser.vpn_required && newUser.allowed_ip_ranges
              ? newUser.allowed_ip_ranges
                  .split(",")
                  .map((ip) => ip.trim())
                  .filter((ip) => ip)
              : [],
        };

        response =
          await medicalProfessionalsService.createCustomMedicalProfessional(
            userData
          );
      } else {
        // Create regular user with simplified security requirements
        userData = {
          username: newUser.username,
          email: newUser.email,
          password: newUser.send_activation_email
            ? undefined
            : newUser.password,
          full_name: newUser.full_name,
          phone_number: newUser.phone_number, // Required for SMS 2FA
          address: newUser.address || null,
          role: newUser.role,
          facility_id: newUser.facility_id
            ? parseInt(newUser.facility_id)
            : null,
          company_name: newUser.company_name || null,
          business_license: newUser.business_license || null,
          specialty: newUser.specialty || null,
          medical_license: newUser.medical_license || null,
          send_welcome_email: newUser.send_welcome_email,
          send_activation_email: newUser.send_activation_email,

          // UPDATED: VPN is optional, IP ranges only if VPN enabled
          vpn_required: newUser.vpn_required || false,
          allowed_ip_ranges:
            newUser.vpn_required && newUser.allowed_ip_ranges
              ? newUser.allowed_ip_ranges
                  .split(",")
                  .map((ip) => ip.trim())
                  .filter((ip) => ip)
              : [],
        };

        response = await userService.createUser(userData);
      }

      // UPDATED: Handle API key display
      if (response && response.generated_api_key) {
        setGeneratedApiKey(response.generated_api_key);
        setSuccess(
          "User created successfully! API Key generated - please save it securely."
        );
      } else {
        setSuccess("User created successfully!");
      }

      await fetchData();
      setOpenCreateDialog(false);
      // Don't reset form immediately if we need to show API key
      if (!response?.generated_api_key) {
        resetForm();
      }

      setTimeout(() => setSuccess(""), 5000);
    } catch (error) {
      console.error("Error creating user:", error);
      setError(error.message || "Failed to create user");
    }
  };

  // UPDATED: Reset form with simplified defaults
  const resetForm = () => {
    setNewUser({
      // Basic user info
      username: "",
      email: "",
      password: "",
      full_name: "",
      phone_number: "",
      address: "",
      role: "facility_admin",

      // Facility assignment
      facility_id: !isSuperAdmin ? getUserFacilityId() : "",

      // Admin-specific fields
      company_name: "",
      business_license: "",

      // Medical professional-specific fields
      specialty: "",
      medical_license: "",
      mp_category: "nurse",
      medical_license_number: "",
      npi_number: "",

      // UPDATED: Security settings - VPN optional
      vpn_required: false,
      allowed_ip_ranges: "",
      setup_2fa: true,

      // Email settings
      send_welcome_email: true,
      send_activation_email: false,
    });
    setValidationErrors({});
    setGeneratedApiKey(""); // Reset API key
  };

  const handleAssignMP = async () => {
    try {
      await userService.assignMedicalProfessional(selectedMP, selectedAdmin);
      setSuccess("Medical professional assigned successfully");
      await fetchData();
      setOpenAssignDialog(false);
      setSelectedMP(null);
      setSelectedAdmin(null);
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error assigning medical professional:", error);
      setError(error.message || "Failed to assign medical professional");
    }
  };

  const getStatusChip = (status, isActive = true) => {
    if (!isActive) {
      return <Chip label="Inactive" color="error" size="small" />;
    }

    switch (status) {
      case "active":
        return <Chip label="Active" color="success" size="small" />;
      case "pending":
        return <Chip label="Pending" color="warning" size="small" />;
      case "inactive":
        return <Chip label="Inactive" color="default" size="small" />;
      case "rejected":
        return <Chip label="Rejected" color="error" size="small" />;
      default:
        return (
          <Chip label={status || "Unknown"} color="default" size="small" />
        );
    }
  };

  const getRoleChip = (role) => {
    switch (role) {
      case "super_admin":
        return <Chip label="Super Admin" color="error" size="small" />;
      case "admin":
        return <Chip label="Admin" color="primary" size="small" />;
      case "facility_admin":
        return <Chip label="Facility Admin" color="success" size="small" />;
      case "medical_professional":
        return (
          <Chip label="Medical Professional" color="secondary" size="small" />
        );
      default:
        return <Chip label={role || "User"} color="default" size="small" />;
    }
  };

  const getFacilityName = (facilityId) => {
    const facility = facilities.find((f) => f.id === facilityId);
    return facility ? facility.company_name : "Unknown Facility";
  };

  const filteredUsers = (userList) => {
    if (!searchQuery) return userList;

    const query = searchQuery.toLowerCase();
    return userList.filter(
      (user) =>
        user.full_name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.username?.toLowerCase().includes(query) ||
        user.role?.toLowerCase().includes(query) ||
        getFacilityName(user.facility_id)?.toLowerCase().includes(query)
    );
  };

  const getCurrentTabData = () => {
    switch (tabValue) {
      case 0:
        return filteredUsers(users.all);
      case 1:
        return filteredUsers(users.pending);
      case 2:
        return filteredUsers(users.admins);
      case 3:
        return filteredUsers(users.medicalProfessionals);
      default:
        return [];
    }
  };

  const currentData = getCurrentTabData();
  const paginatedData = currentData.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );

  // Copy to clipboard function
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Show brief success message
      const originalTitle = document.title;
      document.title = "✅ Copied to clipboard!";
      setTimeout(() => {
        document.title = originalTitle;
      }, 2000);
    });
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. Super Admin privileges required.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight="bold">
          User Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setOpenCreateDialog(true)}
        >
          Create User
        </Button>
      </Box>

      <Paper elevation={3}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            label={`All Users (${users.all.length})`}
            icon={<PeopleIcon />}
            iconPosition="start"
          />
          <Tab
            label={`Pending (${users.pending.length})`}
            icon={<PersonIcon />}
            iconPosition="start"
          />
          <Tab
            label={`Admins (${users.admins.length})`}
            icon={<PersonIcon />}
            iconPosition="start"
          />
          <Tab
            label={`Medical Professionals (${users.medicalProfessionals.length})`}
            icon={<MedicalIcon />}
            iconPosition="start"
          />
        </Tabs>

        {/* Search Bar */}
        <Box sx={{ p: 3, borderBottom: 1, borderColor: "divider" }}>
          <TextField
            fullWidth
            placeholder="Search users by name, email, username, role, or facility"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* User Tables */}
        <TabPanel value={tabValue} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Facility</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ width: 40, height: 40 }}>
                          {user.full_name?.charAt(0) || "U"}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {user.full_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            @{user.username}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{getRoleChip(user.role)}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <FacilityIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {getFacilityName(user.facility_id)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(user.status, user.is_active)}
                    </TableCell>
                    <TableCell>
                      {user.created_at
                        ? new Date(user.created_at).toLocaleDateString()
                        : "N/A"}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit User">
                        <IconButton
                          color="primary"
                          size="small"
                          component={Link}
                          to={`/users/${user.id}`}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {(isSuperAdmin ||
                        (currentUser.role === "facility_admin" &&
                          user.facility_id === getUserFacilityId())) &&
                        user.id !== currentUser?.id && (
                          <Tooltip title="Reset Password">
                            <IconButton
                              color="warning"
                              size="small"
                              onClick={() =>
                                setResetPasswordDialog({ open: true, user })
                              }
                            >
                              <VpnIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      {user.status === "pending" && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton
                              color="success"
                              size="small"
                              onClick={() => handleApproveUser(user.id)}
                            >
                              <ApproveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleRejectUser(user.id)}
                            >
                              <RejectIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {user.id !== currentUser?.id && (
                        <Tooltip title="Delete User">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() =>
                              setDeleteDialog({ open: true, user })
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={currentData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Facility</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ width: 40, height: 40 }}>
                          {user.full_name?.charAt(0) || "U"}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {user.full_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{getRoleChip(user.role)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getFacilityName(user.facility_id)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {user.role === "admin" && user.company_name && (
                        <Typography variant="body2">
                          Company: {user.company_name}
                        </Typography>
                      )}
                      {user.role === "medical_professional" &&
                        user.specialty && (
                          <Typography variant="body2">
                            Specialty: {user.specialty}
                          </Typography>
                        )}
                      {user.phone_number && (
                        <Typography
                          variant="body2"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <PhoneIcon fontSize="small" />
                          {user.phone_number}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Approve">
                        <IconButton
                          color="success"
                          size="small"
                          onClick={() => handleApproveUser(user.id)}
                        >
                          <ApproveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleRejectUser(user.id)}
                        >
                          <RejectIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={currentData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Admin</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Facility</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Medical Professionals</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((admin) => (
                  <TableRow key={admin.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ width: 40, height: 40 }}>
                          {admin.full_name?.charAt(0) || "A"}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {admin.full_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {admin.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{admin.company_name || "N/A"}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getFacilityName(admin.facility_id)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(admin.status, admin.is_active)}
                    </TableCell>
                    <TableCell>
                      {
                        users.medicalProfessionals.filter(
                          (mp) => mp.assigned_admin_id === admin.id
                        ).length
                      }{" "}
                      Assigned
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setSelectedUser(admin);
                          setOpenAssignDialog(true);
                          setSelectedAdmin(admin.id);
                        }}
                      >
                        Assign MP
                      </Button>
                      <Tooltip title="Edit">
                        <IconButton
                          color="primary"
                          size="small"
                          component={Link}
                          to={`/users/${admin.id}`}
                          sx={{ ml: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={currentData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Medical Professional</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Specialty</TableCell>
                  <TableCell>Facility</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((mp) => (
                  <TableRow key={mp.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ width: 40, height: 40 }}>
                          {mp.full_name?.charAt(0) || "M"}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {mp.full_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {mp.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={mp.mp_category || "N/A"}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{mp.specialty || "N/A"}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getFacilityName(mp.facility_id)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(mp.status, mp.is_active)}
                    </TableCell>
                    <TableCell>
                      {mp.assigned_admin_id
                        ? users.admins.find(
                            (a) => a.id === mp.assigned_admin_id
                          )?.full_name || "Unknown"
                        : "Not Assigned"}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setSelectedUser(mp);
                          setOpenAssignDialog(true);
                          setSelectedMP(mp.id);
                        }}
                        disabled={mp.status !== "active"}
                      >
                        Assign
                      </Button>
                      <Tooltip title="Edit">
                        <IconButton
                          color="primary"
                          size="small"
                          component={Link}
                          to={`/users/${mp.id}`}
                          sx={{ ml: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={currentData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </TabPanel>
      </Paper>

      {/* UPDATED: Enhanced Create User Dialog */}
      <Dialog
        open={openCreateDialog}
        onClose={() => {
          setOpenCreateDialog(false);
          resetForm();
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <AddIcon />
            Create New User
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Basic Information Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <PersonIcon />
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid  size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Full Name"
                      value={newUser.full_name}
                      onChange={(e) =>
                        setNewUser({ ...newUser, full_name: e.target.value })
                      }
                      fullWidth
                      required
                      error={!!validationErrors.full_name}
                      helperText={validationErrors.full_name}
                    />
                  </Grid>
                  <Grid  size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Username"
                      value={newUser.username}
                      onChange={(e) =>
                        setNewUser({ ...newUser, username: e.target.value })
                      }
                      fullWidth
                      required
                      error={!!validationErrors.username}
                      helperText={validationErrors.username}
                    />
                  </Grid>
                  <Grid  size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Email"
                      type="email"
                      value={newUser.email}
                      onChange={(e) =>
                        setNewUser({ ...newUser, email: e.target.value })
                      }
                      fullWidth
                      required
                      error={!!validationErrors.email}
                      helperText={validationErrors.email}
                    />
                  </Grid>
                  <Grid  size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      value={newUser.password}
                      onChange={(e) =>
                        setNewUser({ ...newUser, password: e.target.value })
                      }
                      fullWidth
                      required={!newUser.send_activation_email}
                      disabled={newUser.send_activation_email}
                      error={!!validationErrors.password}
                      helperText={
                        validationErrors.password ||
                        (newUser.send_activation_email
                          ? "User will set password via activation email"
                          : "Minimum 8 characters")
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid  size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Phone Number"
                      value={newUser.phone_number}
                      onChange={(e) =>
                        setNewUser({ ...newUser, phone_number: e.target.value })
                      }
                      fullWidth
                      required
                      placeholder="+1 (555) 123-4567"
                      error={!!validationErrors.phone_number}
                      helperText={
                        validationErrors.phone_number ||
                        "Required for SMS 2FA during login"
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid  size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Address"
                      value={newUser.address}
                      onChange={(e) =>
                        setNewUser({ ...newUser, address: e.target.value })
                      }
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Role and Facility Assignment Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <FacilityIcon />
                  Role & Facility Assignment
                </Typography>
                <Grid container spacing={2}>
                  <Grid  size={{ xs: 12, sm: 6 }}>
                    <FormControl
                      fullWidth
                      required
                      error={!!validationErrors.role}
                    >
                      <InputLabel>Role</InputLabel>
                      <Select
                        value={newUser.role}
                        onChange={(e) =>
                          setNewUser({ ...newUser, role: e.target.value })
                        }
                        label="Role"
                      >
                        {getUserRoles().map((role) => (
                          <MenuItem key={role.value} value={role.value}>
                            {role.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {validationErrors.role && (
                        <FormHelperText>{validationErrors.role}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid  size={{ xs: 12, sm: 6 }}>
                    <FormControl
                      fullWidth
                      required={newUser.role !== "super_admin"}
                      error={!!validationErrors.facility_id}
                    >
                      <InputLabel>Facility</InputLabel>
                      <Select
                        value={newUser.facility_id}
                        onChange={(e) =>
                          setNewUser({
                            ...newUser,
                            facility_id: e.target.value,
                          })
                        }
                        label="Facility"
                        disabled={!isSuperAdmin && getUserFacilityId()}
                      >
                        <MenuItem value="">Select Facility</MenuItem>
                        {facilities.map((facility) => (
                          <MenuItem key={facility.id} value={facility.id}>
                            {facility.company_name}
                          </MenuItem>
                        ))}
                      </Select>
                      {validationErrors.facility_id && (
                        <FormHelperText>
                          {validationErrors.facility_id}
                        </FormHelperText>
                      )}
                      {!isSuperAdmin && (
                        <FormHelperText>
                          Auto-assigned to your facility
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Role-Specific Fields */}
            {(newUser.role === "admin" ||
              newUser.role === "facility_admin") && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <PersonIcon />
                    Admin Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid  size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Company Name"
                        value={newUser.company_name}
                        onChange={(e) =>
                          setNewUser({
                            ...newUser,
                            company_name: e.target.value,
                          })
                        }
                        fullWidth
                        required
                        error={!!validationErrors.company_name}
                        helperText={validationErrors.company_name}
                      />
                    </Grid>
                    <Grid  size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Business License"
                        value={newUser.business_license}
                        onChange={(e) =>
                          setNewUser({
                            ...newUser,
                            business_license: e.target.value,
                          })
                        }
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {newUser.role === "medical_professional" && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <MedicalIcon />
                    Medical Professional Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid  size={{ xs: 12, sm: 6 }}>
                      <FormControl
                        fullWidth
                        required
                        error={!!validationErrors.mp_category}
                      >
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={newUser.mp_category}
                          onChange={(e) =>
                            setNewUser({
                              ...newUser,
                              mp_category: e.target.value,
                            })
                          }
                          label="Category"
                        >
                          {MP_CATEGORIES.map((category) => (
                            <MenuItem
                              key={category.value}
                              value={category.value}
                            >
                              {category.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {validationErrors.mp_category && (
                          <FormHelperText>
                            {validationErrors.mp_category}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid  size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Specialty"
                        value={newUser.specialty}
                        onChange={(e) =>
                          setNewUser({ ...newUser, specialty: e.target.value })
                        }
                        fullWidth
                        required
                        error={!!validationErrors.specialty}
                        helperText={validationErrors.specialty}
                      />
                    </Grid>
                    <Grid  size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Medical License Number"
                        value={newUser.medical_license_number}
                        onChange={(e) =>
                          setNewUser({
                            ...newUser,
                            medical_license_number: e.target.value,
                          })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid  size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="NPI Number"
                        value={newUser.npi_number}
                        onChange={(e) =>
                          setNewUser({ ...newUser, npi_number: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* UPDATED: Security Settings Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <SecurityIcon />
                  Security Settings
                </Typography>
                <Grid container spacing={2}>
                  <Grid  size={{ xs: 12 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={newUser.vpn_required}
                          onChange={(e) =>
                            setNewUser({
                              ...newUser,
                              vpn_required: e.target.checked,
                            })
                          }
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <VpnIcon fontSize="small" />
                          Require VPN Connection (Optional)
                        </Box>
                      }
                    />
                    <FormHelperText>
                      VPN can be enabled optionally for additional security
                    </FormHelperText>
                  </Grid>

                  {/* IP Ranges - Only show if VPN enabled */}
                  {newUser.vpn_required && (
                    <Grid  size={{ xs: 12 }}>
                      <TextField
                        label="Allowed IP Ranges (Optional)"
                        value={newUser.allowed_ip_ranges}
                        onChange={(e) =>
                          setNewUser({
                            ...newUser,
                            allowed_ip_ranges: e.target.value,
                          })
                        }
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="192.168.1.0/24, 10.0.0.0/8 (leave empty to allow all IPs)"
                        helperText="Enter IP addresses or CIDR ranges separated by commas. Leave empty to allow all IPs when VPN is enabled."
                        error={!!validationErrors.allowed_ip_ranges}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SecurityIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                      {validationErrors.allowed_ip_ranges && (
                        <FormHelperText error>
                          {validationErrors.allowed_ip_ranges}
                        </FormHelperText>
                      )}
                    </Grid>
                  )}

                  <Grid  size={{ xs: 12 }}>
                    <Alert severity="success">
                      <strong>Automatic Setup:</strong>
                      <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                        <li>
                          ✅ SMS 2FA is enabled automatically with the provided
                          phone number
                        </li>
                        <li>
                          ✅ API Key is generated automatically for secure
                          access
                        </li>
                        <li>
                          ✅ User account is immediately active and verified
                        </li>
                        <li>
                          ✅ User can login right away with username/password +
                          SMS code
                        </li>
                      </ul>
                    </Alert>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Email Settings Section */}
            <Card>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <EmailIcon />
                  Email Settings
                </Typography>
                <Grid container spacing={2}>
                  <Grid  size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={newUser.send_welcome_email}
                          onChange={(e) =>
                            setNewUser({
                              ...newUser,
                              send_welcome_email: e.target.checked,
                            })
                          }
                          disabled={newUser.send_activation_email}
                        />
                      }
                      label="Send Welcome Email"
                    />
                  </Grid>
                  <Grid  size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={newUser.send_activation_email}
                          onChange={(e) => {
                            setNewUser({
                              ...newUser,
                              send_activation_email: e.target.checked,
                              send_welcome_email: e.target.checked
                                ? false
                                : newUser.send_welcome_email,
                              password: e.target.checked
                                ? ""
                                : newUser.password,
                            });
                          }}
                        />
                      }
                      label="Send Activation Email (User sets own password)"
                    />
                  </Grid>
                </Grid>
                {newUser.send_activation_email && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    When activation email is enabled, the user will receive an
                    email to set their own password. The password field above
                    will be ignored and welcome email will be disabled.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => {
              setOpenCreateDialog(false);
              resetForm();
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            color="primary"
            disabled={
              !newUser.full_name ||
              !newUser.username ||
              !newUser.email ||
              !newUser.phone_number || // Required for SMS 2FA
              (!newUser.password && !newUser.send_activation_email) ||
              !newUser.role ||
              (!newUser.facility_id && newUser.role !== "super_admin")
            }
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* NEW: API Key Display Dialog */}
      <Dialog
        open={!!generatedApiKey}
        onClose={() => {
          setGeneratedApiKey("");
          resetForm();
        }}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <VpnIcon color="primary" />
            Generated API Key - Save Securely!
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>⚠️ Important:</strong> Save this API key securely. It will
            NOT be shown again!
          </Alert>

          <TextField
            label="Generated API Key"
            value={generatedApiKey}
            fullWidth
            multiline
            rows={3}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copy to clipboard">
                    <IconButton
                      onClick={() => copyToClipboard(generatedApiKey)}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>What to do with this API key:</strong>
            <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
              <li>📧 Send it to the user via secure email</li>
              <li>🔐 Store it in your password manager</li>
              <li>📱 Provide it during user onboarding</li>
              <li>🚫 Never share it through unsecured channels</li>
            </ul>
          </Alert>

          <Typography variant="body2" color="text.secondary">
            The user will need this API key along with their login credentials
            for secure API access. They can login to the web interface with just
            username/password + SMS code.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setGeneratedApiKey("");
              resetForm();
            }}
            variant="contained"
            color="primary"
            fullWidth
          >
            ✅ I've Saved the API Key Securely
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Medical Professional Dialog */}
      <Dialog
        open={openAssignDialog}
        onClose={() => setOpenAssignDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedMP ? "Assign Medical Professional" : "Assign to Admin"}
        </DialogTitle>
        <DialogContent>
          {selectedMP ? (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>Select Admin</InputLabel>
              <Select
                value={selectedAdmin || ""}
                onChange={(e) => setSelectedAdmin(e.target.value)}
                label="Select Admin"
              >
                <MenuItem value="">None</MenuItem>
                {users.admins
                  .filter((admin) => admin.status === "active")
                  .map((admin) => (
                    <MenuItem key={admin.id} value={admin.id}>
                      {admin.full_name} ({admin.company_name || "No Company"})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          ) : (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>Select Medical Professional</InputLabel>
              <Select
                value={selectedMP || ""}
                onChange={(e) => setSelectedMP(e.target.value)}
                label="Select Medical Professional"
              >
                {users.medicalProfessionals
                  .filter((mp) => mp.status === "active")
                  .map((mp) => (
                    <MenuItem key={mp.id} value={mp.id}>
                      {mp.full_name} ({mp.specialty || "No Specialty"})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAssignMP}
            variant="contained"
            color="primary"
            disabled={!selectedMP || !selectedAdmin}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, user: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{deleteDialog.user?.full_name}
            "? This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will permanently remove the user and all associated data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, user: null })}
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resetPasswordDialog.open}
        onClose={() => {
          setResetPasswordDialog({ open: false, user: null });
          setNewPassword("");
          setConfirmNewPassword("");
          setError("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <VpnIcon color="warning" />
            Reset Password for {resetPasswordDialog.user?.full_name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Security Notice:</strong> The user will need to login with
            the new password and verify their phone number for SMS 2FA.
          </Alert>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid  size={{ xs: 12 }}>
              <TextField
                label="New Password"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                required
                helperText="Minimum 8 characters"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid  size={{ xs: 12 }}>
              <TextField
                label="Confirm New Password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                fullWidth
                required
                helperText="Must match the password above"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setResetPasswordDialog({ open: false, user: null });
              setNewPassword("");
              setConfirmNewPassword("");
              setError("");
            }}
            disabled={resetPasswordLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetPassword}
            variant="contained"
            color="warning"
            disabled={
              resetPasswordLoading || !newPassword || !confirmNewPassword
            }
            startIcon={
              resetPasswordLoading ? (
                <CircularProgress size={16} />
              ) : (
                <VpnIcon />
              )
            }
          >
            {resetPasswordLoading ? "Resetting..." : "Reset Password"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
